import React from 'react';
import ContentWrapper from '../Layout/ContentWrapper';
import {Card, CardBody, Button, InputGroup, UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem} from 'reactstrap';
import {API_ROOT} from '../../api-config';
import Swal from 'sweetalert2'
import moment from 'moment';
import 'moment-timezone';
import {getStock, getPendingDocs, invDocAction} from './InventoryFunctions.js';

moment.tz.setDefault("America/Toronto");

const DOC_NAMES = {
    PO: 'Purchase Order', IR: 'Item Receipt', TO: 'Transfer Order',
    TR: 'Inventory Transfer', AJ: 'Inventory Adjustment',
    WO: 'Work Order', AB: 'Assembly Build'
};

const fmt = (v) => {
    const n = parseFloat(v) || 0;
    return (Math.round(n * 100) / 100).toLocaleString('en-CA', {maximumFractionDigits: 2});
};

class InventoryOverview extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            rows: [],
            locations: [],
            location_id: 'all',
            location_name: 'All Locations',
            pending: [],
            loading: true
        };
    }

    componentDidMount() {
        this.loadStock('all');
        this.loadPending();
    }

    loadStock(location_id) {
        getStock(location_id, (data) => {
            this.setState({
                rows: data.rows || [],
                locations: data.locations || this.state.locations,
                location_id: location_id,
                loading: false
            });
        });
    }

    loadPending() {
        getPendingDocs((docs) => {
            this.setState({pending: docs || []});
        });
    }

    changeLocation(location_id, name) {
        this.setState({location_name: name, loading: true});
        this.loadStock(location_id);
    }

    confirm(doc) {
        invDocAction(doc.document_id, 'confirm', {}, () => {
            Swal('Confirmed', doc.doc_no + ' confirmed.', 'success');
            this.loadPending();
            this.loadStock(this.state.location_id);
        }, (msg) => {
            Swal('Error', msg, 'error');
        });
    }

    printDoc(doc) {
        window.open(API_ROOT + '/inventory/document/' + doc.document_id + '/print', '_blank');
    }

    exportCSV() {
        const head = ['Code', 'Description', 'Type', 'On Hand', 'Incoming', 'Committed Out', 'Expected', 'UOM'];
        const lines = this.state.rows.map(r =>
            [r.code, (r.description || '').replace(/"/g, '""'), r.type,
             fmt(r.on_hand), fmt(r.incoming), fmt(r.committed_out), fmt(r.expected), r.uom]
                .map(v => '"' + v + '"').join(','));
        const csv = head.join(',') + '\n' + lines.join('\n');
        const blob = new Blob([csv], {type: 'text/csv'});
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'inventory_stock_' + moment().format('YYYY-MM-DD') + '.csv';
        a.click();
        URL.revokeObjectURL(a.href);
    }

    pendingParty(doc) {
        if (doc.supplier_name) return doc.supplier_name;
        if (doc.doc_type === 'TO') return (doc.from_name || '?') + ' → ' + (doc.to_name || '?');
        return doc.location_name || '';
    }

    render() {
        return (
            <ContentWrapper>
                <div className="content-heading">
                    Inventory
                    <small>Stock overview</small>
                </div>

                {this.state.pending.length > 0 &&
                <Card className="card-default border-warning">
                    <div className="card-header bg-warning">
                        <b>Pending Confirmations ({this.state.pending.length})</b>
                    </div>
                    <CardBody>
                        <table className="table table-sm table-striped">
                            <thead>
                            <tr>
                                <th>Doc</th>
                                <th>Type</th>
                                <th>Date</th>
                                <th>Party</th>
                                <th>Status</th>
                                <th>Items</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {this.state.pending.map(doc =>
                                <tr key={doc.document_id}>
                                    <td><b>{doc.doc_no}</b></td>
                                    <td>{DOC_NAMES[doc.doc_type] || doc.doc_type}</td>
                                    <td>{doc.date ? moment(doc.date).format('MMM D, YYYY') : ''}</td>
                                    <td>{this.pendingParty(doc)}</td>
                                    <td>{doc.status}</td>
                                    <td>
                                        {(doc.lines || []).map(function (l, j) {
                                            return <div key={j}>{l.item_code} × {fmt(l.qty)}</div>;
                                        })}
                                    </td>
                                    <td className="text-right">
                                        <Button color="link" size="sm" title="Print"
                                                onClick={() => this.printDoc(doc)}>
                                            <i className="fas fa-print"></i>
                                        </Button>
                                        <Button color="success" size="sm"
                                                onClick={() => this.confirm(doc)}>Confirm</Button>
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </CardBody>
                </Card>}

                <Card className="card-default">
                    <div className="card-header">
                        <div className="float-right">
                            <InputGroup>
                                <UncontrolledButtonDropdown>
                                    <DropdownToggle caret color="secondary">
                                        {this.state.location_name}
                                    </DropdownToggle>
                                    <DropdownMenu>
                                        <DropdownItem
                                            onClick={() => this.changeLocation('all', 'All Locations')}>
                                            All Locations
                                        </DropdownItem>
                                        <DropdownItem divider/>
                                        {this.state.locations.map(l =>
                                            <DropdownItem key={l.inventory_source_id}
                                                          onClick={() => this.changeLocation(l.inventory_source_id, l.name)}>
                                                {l.name}
                                            </DropdownItem>
                                        )}
                                    </DropdownMenu>
                                </UncontrolledButtonDropdown>
                                <Button color="secondary" size="sm" onClick={this.exportCSV.bind(this)}>
                                    <i className="fas fa-file-csv"></i> Export CSV
                                </Button>
                            </InputGroup>
                        </div>
                        <b>Stock on Hand</b>
                    </div>
                    <CardBody>
                        <table className="table table-striped table-hover">
                            <thead>
                            <tr>
                                <th>Code</th>
                                <th>Description</th>
                                <th>Type</th>
                                <th className="text-right">On Hand</th>
                                <th className="text-right">Incoming</th>
                                {this.state.location_id !== 'all' &&
                                    <th className="text-right">Committed Out</th>}
                                <th className="text-right">Expected</th>
                            </tr>
                            </thead>
                            <tbody>
                            {this.state.rows.map(row =>
                                <tr key={row.item_id}>
                                    <td><b>{row.code}</b></td>
                                    <td>{row.description}</td>
                                    <td>{row.type}</td>
                                    <td className="text-right">
                                        {fmt(row.on_hand)} {row.uom !== 'Each' ?
                                        <span className="text-muted"> {row.uom}</span> : null}
                                    </td>
                                    <td className="text-right">{fmt(row.incoming)}</td>
                                    {this.state.location_id !== 'all' &&
                                        <td className="text-right">{fmt(row.committed_out)}</td>}
                                    <td className="text-right"><b>{fmt(row.expected)}</b></td>
                                </tr>
                            )}
                            {this.state.rows.length === 0 &&
                                <tr>
                                    <td colSpan="7" className="text-center text-muted">
                                        {this.state.loading ? 'Loading…' : 'No stock to show.'}
                                    </td>
                                </tr>}
                            </tbody>
                        </table>
                    </CardBody>
                </Card>
            </ContentWrapper>
        );
    }
}

export default InventoryOverview;
